import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { formatDateNL, formatHoursHM, getISOWeek, isoWeekDates, navigateIsoWeek } from '@/lib/format';
import {
  ChevronLeft, ChevronRight, LogOut, CalendarDays, Flag, Clock,
  ClipboardList, Bell, User as UserIcon, Loader2, Check, Save,
  AlertCircle, MapPin, Plane, Trash2, Send, History, Stethoscope, Briefcase, QrCode,
  BookOpen, Download, FileText,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ProbleemMeldenDialog } from '@/components/ProbleemMeldenDialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import DocumentenSection from '@/components/DocumentenSection';

const DAY_NAMES = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];

function WeekNavigation({ year, week, setYear, setWeek }) {
  const navigateWeek = (delta) => {
    const { year: y, week: w } = navigateIsoWeek(year, week, delta);
    setYear(y);
    setWeek(w);
  };
  return (
    <div className="inline-flex items-center rounded-md border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => navigateWeek(-1)}
        data-testid="portal-week-prev"
        className="h-9 w-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 rounded-l-md"
      >
        <ChevronLeft size={16} />
      </button>
      <div className="px-4 text-sm font-medium text-slate-700" data-testid="portal-week-label">
        Week {week} / {year}
      </div>
      <button
        type="button"
        onClick={() => navigateWeek(1)}
        data-testid="portal-week-next"
        className="h-9 w-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 rounded-r-md"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

function RoosterTab({ year, week, setYear, setWeek }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/my-roster?year=${year}&week=${week}`);
      setAssignments(data.assignments || []);
    } catch (e) {
      console.error('[portal] load roster failed', e);
    } finally {
      setLoading(false);
    }
  }, [year, week]);

  useEffect(() => { load(); }, [load]);

  const weekDates = useMemo(() => isoWeekDates(year, week), [year, week]);
  const byDay = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      m[a.day_of_week] = m[a.day_of_week] || [];
      m[a.day_of_week].push(a);
    }
    return m;
  }, [assignments]);

  const totalNet = assignments.reduce((s, a) => s + (a.duration_net || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <WeekNavigation year={year} week={week} setYear={setYear} setWeek={setWeek} />
        <Badge variant="outline" className="text-xs">
          {assignments.length} shifts · {formatHoursHM(totalNet)}
        </Badge>
      </div>
      {loading ? (
        <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Laden…
        </div>
      ) : (
        <div className="space-y-2">
          {DAY_NAMES.map((name, idx) => {
            const dow = idx + 1;
            const dayList = byDay[dow] || [];
            const date = weekDates[idx];
            return (
              <div
                key={dow}
                className={`rounded-lg border ${dayList.length ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/30'} p-3`}
                data-testid={`portal-day-${dow}`}
              >
                <div className="flex justify-between items-baseline mb-1.5">
                  <h4 className="text-sm font-semibold text-slate-900">{name}</h4>
                  <span className="text-[11px] text-slate-400 tabular-nums">{formatDateNL(date.toISOString().slice(0, 10))}</span>
                </div>
                {dayList.length === 0 ? (
                  <p className="text-xs text-slate-400">— Vrij</p>
                ) : (
                  <div className="space-y-1.5">
                    {dayList.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded bg-brand-amber/5 border border-brand-amber/20 px-2.5 py-1.5">
                        <div className="flex items-center gap-2">
                          <Clock size={12} className="text-brand-amber-dark" />
                          <span className="text-sm tabular-nums font-medium text-slate-900">
                            {a.start_time}–{a.end_time}
                          </span>
                          {a.is_hulpvraag && (
                            <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">
                              <Flag size={9} className="mr-0.5" />Hulpvraag
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                          <MapPin size={11} />
                          {a.location_id}
                          <span className="text-slate-400">·</span>
                          {formatHoursHM(a.duration_net)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UrenTab({ year, week, setYear, setWeek }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/portal/my-hours?year=${year}&week=${week}`);
      setData(d);
    } catch (e) {
      console.error('[portal] load hours failed', e);
    } finally {
      setLoading(false);
    }
  }, [year, week]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <WeekNavigation year={year} week={week} setYear={setYear} setWeek={setWeek} />
      </div>
      {loading ? (
        <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Laden…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2" data-testid="portal-uren-totals">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-[10px] uppercase text-slate-500">Gepland</p>
              <p className="text-xl font-bold tabular-nums text-slate-900">{formatHoursHM(totals.planned || 0)}</p>
              <p className="text-[10px] text-slate-400">Contract: {formatHoursHM(totals.contract || 0)}</p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[10px] uppercase text-emerald-600">Gewerkt</p>
              <p className="text-xl font-bold tabular-nums text-emerald-700">{formatHoursHM(totals.worked || 0)}</p>
              <p className="text-[10px] text-emerald-600">Goedgekeurd: {formatHoursHM(totals.approved || 0)}</p>
            </div>
          </div>
          {data?.days?.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">Geen uren deze week.</div>
          ) : (
            <div className="space-y-2">
              {data?.days?.map((d) => (
                <div key={d.date} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`portal-uren-day-${d.date}`}>
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-sm font-medium text-slate-900">{formatDateNL(d.date)}</span>
                    <div className="flex gap-1.5 text-[10px]">
                      {d.planned > 0 && <Badge variant="outline" className="text-[10px]">Gepland: {formatHoursHM(d.planned)}</Badge>}
                      {d.worked > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Gewerkt: {formatHoursHM(d.worked)}</Badge>}
                      {d.rejected > 0 && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Niet versch.: {formatHoursHM(d.rejected)}</Badge>}
                    </div>
                  </div>
                  {d.shifts.length > 0 && (
                    <ul className="text-[11px] text-slate-500 space-y-0.5">
                      {d.shifts.map((s) => (
                        <li key={s.assignment_id} className="flex justify-between">
                          <span className="tabular-nums">{s.start_time}–{s.end_time} · {s.location_id}</span>
                          <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MijnQrTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get('/portal/my-qr');
        setData(d);
      } catch (e) {
        console.error('[portal] load my-qr failed', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="py-12 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Laden…</div>;
  }
  if (error || !data) {
    return <div className="py-12 text-center text-rose-600 text-sm flex items-center justify-center gap-2"><AlertCircle size={16} /> QR-code laden mislukt.</div>;
  }

  return (
    <div className="flex flex-col items-center" data-testid="portal-my-qr">
      <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-brand-navy/5 px-3 py-1 text-xs font-medium text-brand-navy">
          <QrCode size={13} /> Inklok-QR
        </div>
        <h3 className="mt-2 text-lg font-bold text-slate-900" data-testid="portal-qr-name">{data.name}</h3>
        <p className="text-sm tabular-nums text-slate-500" data-testid="portal-qr-pnr">Personeelsnummer {data.personeelsnummer}</p>
        <div className="mx-auto mt-5 w-fit rounded-2xl border-4 border-brand-navy bg-white p-4" data-testid="portal-qr-code">
          <QRCodeSVG
            value={data.qr_token}
            size={240}
            level="M"
            marginSize={2}
            className="h-auto w-full max-w-[240px]"
          />
        </div>
        <p className="mt-5 text-sm text-slate-600">
          Houd deze QR-code voor de scanner op het inklokstation om in- en uit te klokken.
        </p>
        <p className="mt-1 text-[11px] text-slate-400">
          Deze code is persoonlijk. Deel hem niet met collega's.
        </p>
      </div>
    </div>
  );
}


function VacaturesTab() {
  const [vacs, setVacs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { toast } = useToast();
  const TYPE = { vast: 'Vast dienstverband', tijdelijk: 'Tijdelijk', oproep: 'Oproepkracht' };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/portal/vacatures');
        setVacs(data.vacatures || []);
      } catch (e) {
        /* read-only awareness — fail silently */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const expressInterest = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/portal/vacatures/${id}/interesse`);
      setVacs((prev) => prev.map((v) => (v.id === id ? { ...v, interesse_gegeven: true } : v)));
      toast({ title: 'Interesse doorgegeven', description: 'Je leidinggevende is op de hoogte gebracht.' });
    } catch (e) {
      toast({ title: 'Kon interesse niet doorgeven', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500" data-testid="portal-vacatures-loading">
        <Loader2 size={16} className="mr-2 animate-spin" /> Laden…
      </div>
    );
  }
  if (vacs.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="portal-vacatures-empty">
        <Briefcase size={30} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm font-medium text-slate-700">Geen openstaande vacatures</p>
        <p className="mt-1 text-xs text-slate-500">Er zijn op dit moment geen interne openingen.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="portal-vacatures">
      <div className="rounded-lg bg-brand-navy/5 px-3 py-2 text-xs text-brand-navy">
        Openstaande vacatures bij Truckwash1. Interesse? Geef het door — je leidinggevende neemt contact op.
      </div>
      {vacs.map((v) => (
        <div
          key={v.id}
          data-testid={`portal-vacature-${v.id}`}
          className="rounded-xl border border-slate-200 bg-white p-4"
        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">{v.title}</h3>
            <span className="shrink-0 rounded-full bg-brand-amber/15 px-2 py-0.5 text-[10px] font-medium text-brand-amber-dark">
              {TYPE[v.employment_type] || v.employment_type || 'Vacature'}
            </span>
          </div>
          {v.functie && <p className="mt-0.5 text-xs text-slate-500">{v.functie}</p>}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-600">
            {(v.location_name || v.plaats) && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} className="text-brand-navy" /> {v.location_name || v.plaats}
              </span>
            )}
            {v.hours != null && (
              <span className="inline-flex items-center gap-1">
                <Clock size={12} className="text-brand-navy" /> {v.hours} uur p/w
              </span>
            )}
          </div>
          <div className="mt-3">
            {v.interesse_gegeven ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700"
                data-testid={`portal-interesse-done-${v.id}`}
              >
                <Check size={13} /> Interesse doorgegeven
              </span>
            ) : (
              <Button
                size="sm"
                variant="outline"
                data-testid={`portal-interesse-btn-${v.id}`}
                disabled={busyId === v.id}
                onClick={() => expressInterest(v.id)}
                className="h-8 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white text-xs"
              >
                {busyId === v.id ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Send size={13} className="mr-1.5" />}
                Ik heb interesse
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function InstructiesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/portal/my-instructiebladen')
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const download = async (item) => {
    setBusyId(item.id);
    try {
      const resp = await api.get(`/portal/my-instructiebladen/${item.id}/download`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: item.mime_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('[portal] instructieblad download failed', e);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Laden…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="portal-instructies-empty">
        <BookOpen size={32} className="mx-auto mb-2 text-slate-300" />
        <p>Nog geen instructiebladen voor je beschikbaar.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="portal-instructies-list">
      {items.map((it) => (
        <div key={it.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={`portal-instructie-row-${it.id}`}>
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-brand-navy flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{it.title}</p>
              <p className="text-[11px] text-slate-500 truncate">{it.filename}</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busyId === it.id}
            onClick={() => download(it)}
            data-testid={`portal-instructie-download-${it.id}`}
            className="h-8 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white text-xs flex-shrink-0"
          >
            {busyId === it.id ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Download size={13} className="mr-1.5" />}
            Download
          </Button>
        </div>
      ))}
    </div>
  );
}



function MeldingenTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/portal/my-notifications');
      setItems(data.items || []);
    } catch (e) {
      console.error('[portal] load notifications failed', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/portal/my-notifications/${id}/read`);
      load();
    } catch {
      toast({ title: 'Bijwerken mislukt', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-2">
      {loading ? (
        <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Laden…
        </div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center">
          <Bell size={28} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Geen meldingen.</p>
        </div>
      ) : (
        items.map((n) => {
          const isUnread = !n.read_at;
          return (
            <div
              key={n.id}
              data-testid={`portal-melding-${n.id}`}
              className={`rounded-lg border p-3 ${isUnread ? 'border-brand-amber/30 bg-brand-amber/5' : 'border-slate-200 bg-white'}`}
            >
              <div className="flex justify-between items-start gap-2 mb-1">
                <h4 className={`text-sm ${isUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                  {n.title}
                </h4>
                {isUnread && (
                  <button
                    type="button"
                    data-testid={`portal-mark-read-${n.id}`}
                    onClick={() => markRead(n.id)}
                    className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
                    aria-label="Markeer als gelezen"
                  >
                    <Check size={12} />
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-600">{n.body}</p>
              <p className="text-[10px] text-slate-400 mt-1">
                {new Date(n.created_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
              </p>
            </div>
          );
        })
      )}
    </div>
  );
}

function ProfielTab({ employee }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ telefoon: '', noodcontact_naam: '', noodcontact_telefoon: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/portal/my-profile');
      setProfile(data);
      setForm({
        telefoon: data.telefoon || '',
        noodcontact_naam: data.noodcontact_naam || '',
        noodcontact_telefoon: data.noodcontact_telefoon || '',
      });
    } catch (e) {
      console.error('[portal] load profile failed', e);
      toast({ title: 'Profiel laden mislukt', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/portal/my-profile', form);
      toast({ title: 'Profiel bijgewerkt' });
      await load();
    } catch (e) {
      toast({ title: 'Opslaan mislukt', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return <div className="py-10 text-center text-slate-500"><Loader2 size={16} className="animate-spin inline mr-2" /> Laden…</div>;
  }

  return (
    <div className="space-y-4" data-testid="portal-profile">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Mijn gegevens</h3>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><span className="text-slate-500">Naam:</span> <span className="font-medium text-slate-900 block">{profile.name}</span></div>
          <div><span className="text-slate-500">Personeelsnummer:</span> <span className="font-medium tabular-nums text-slate-900 block">{profile.personeelsnummer}</span></div>
          <div><span className="text-slate-500">E-mail:</span> <span className="font-medium text-slate-900 block">{profile.email || '—'}</span></div>
          <div><span className="text-slate-500">Adres:</span> <span className="font-medium text-slate-900 block">{profile.adres ? `${profile.adres} ${profile.huisnummer || ''}` : '—'}</span></div>
          <div><span className="text-slate-500">Postcode/Plaats:</span> <span className="font-medium text-slate-900 block">{profile.postcode || ''} {profile.woonplaats || ''}</span></div>
          <div><span className="text-slate-500">Contract:</span> <span className="font-medium text-slate-900 block">{profile.contract_type || '—'} · {profile.contract_hours || 0}h</span></div>
          <div><span className="text-slate-500">In dienst:</span> <span className="font-medium text-slate-900 block">{profile.datum_in_dienst || '—'}</span></div>
          <div><span className="text-slate-500">Rol:</span> <span className="font-medium text-slate-900 block">{profile.role || '—'}</span></div>
        </div>
        <p className="text-[10px] text-slate-400 mt-3">
          Wijzigingen in naam, adres of contract? Neem contact op met je bedrijfsleider.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Bewerkbare gegevens</h3>
        <div className="space-y-3">
          <div>
            <Label htmlFor="p-tel" className="text-xs">Telefoonnummer</Label>
            <Input
              id="p-tel" data-testid="portal-input-telefoon"
              value={form.telefoon}
              onChange={(e) => setForm((f) => ({ ...f, telefoon: e.target.value }))}
              placeholder="06-12345678"
            />
          </div>
          <div>
            <Label htmlFor="p-noodn" className="text-xs">Noodcontact (naam)</Label>
            <Input
              id="p-noodn" data-testid="portal-input-noodcontact-naam"
              value={form.noodcontact_naam}
              onChange={(e) => setForm((f) => ({ ...f, noodcontact_naam: e.target.value }))}
              placeholder="bv. Partner/Familie"
            />
          </div>
          <div>
            <Label htmlFor="p-noodt" className="text-xs">Noodcontact (telefoon)</Label>
            <Input
              id="p-noodt" data-testid="portal-input-noodcontact-tel"
              value={form.noodcontact_telefoon}
              onChange={(e) => setForm((f) => ({ ...f, noodcontact_telefoon: e.target.value }))}
              placeholder="06-12345678"
            />
          </div>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="portal-profile-save"
            className="bg-brand-navy hover:bg-brand-navy/90 text-white"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
            Opslaan
          </Button>
        </div>
      </div>

      {/* GDPR transparantie — eigen documenten read-only */}
      {employee && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="portal-my-documents">
          <DocumentenSection
            employeeId={employee.id}
            canEdit={false}
            isPortal
            listUrl="/portal/my-documents"
            downloadUrlBuilder={(id) => `/portal/my-documents/${id}/download`}
          />
          <p className="text-[10px] text-slate-400 mt-3">
            Documenten kunnen alleen door je bedrijfsleider toegevoegd of gewijzigd worden.
          </p>
        </div>
      )}
    </div>
  );
}

function VerlofTab() {
  const VERLOF_TYPES = [
    { value: 'vakantie', label: 'Vakantie' },
    { value: 'bijzonder_verlof', label: 'Bijzonder verlof' },
    { value: 'onbetaald', label: 'Onbetaald verlof' },
  ];
  const TYPE_LABEL = Object.fromEntries(VERLOF_TYPES.map((t) => [t.value, t.label]));
  const STATUS = {
    in_afwachting: { label: 'In afwachting', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    goedgekeurd: { label: 'Goedgekeurd', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    afgewezen: { label: 'Afgewezen', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  };

  const today = new Date().toISOString().slice(0, 10);
  const blank = { type: 'vakantie', start_date: today, end_date: today, day_part: 'hele_dag', reason: '' };
  const [form, setForm] = useState(blank);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vacationBalance, setVacationBalance] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [{ data }, { data: me }] = await Promise.all([
        api.get('/portal/my-leave-requests'),
        api.get('/portal/me'),
      ]);
      setItems(data.items || []);
      setVacationBalance(me?.vacation_balance ?? null);
    } catch (e) {
      console.error('[portal] load leave requests failed', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.end_date < form.start_date) {
      toast({ title: 'Einddatum moet ≥ startdatum zijn', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/portal/my-leave-request', { ...form, reason: form.reason || null });
      toast({ title: 'Aanvraag verstuurd', description: 'Je verlofaanvraag staat in afwachting van goedkeuring.' });
      setForm(blank);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast({ title: 'Versturen mislukt', description: typeof detail === 'string' ? detail : undefined, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async (id) => {
    try {
      await api.delete(`/portal/my-leave-request/${id}`);
      toast({ title: 'Aanvraag ingetrokken' });
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast({ title: 'Intrekken mislukt', description: typeof detail === 'string' ? detail : undefined, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="portal-verlof">
      {/* Indicatief verlofsaldo */}
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="portal-verlof-saldo">
        <p className="text-sm font-semibold text-sky-900">
          Saldo: {vacationBalance === null || vacationBalance === undefined ? 'onbekend' : `${Number(vacationBalance).toLocaleString('nl-NL', { maximumFractionDigits: 2 })} uur`}
        </p>
        <p className="mt-0.5 text-[11px] text-sky-700">
          Indicatief saldo — opname wordt nog niet automatisch afgeboekt; het loonsysteem is leidend.
        </p>
      </div>

      {/* Submit form */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
          <Plane size={15} className="text-brand-navy" /> Verlof aanvragen
        </h3>
        <div className="space-y-3">
          <div>
            <Label htmlFor="v-type" className="text-xs">Type</Label>
            <select
              id="v-type" data-testid="portal-verlof-type"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="mt-1 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy/30"
            >
              {VERLOF_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="v-start" className="text-xs">Van</Label>
              <Input
                id="v-start" type="date" data-testid="portal-verlof-start"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date }))}
              />
            </div>
            <div>
              <Label htmlFor="v-end" className="text-xs">Tot en met</Label>
              <Input
                id="v-end" type="date" data-testid="portal-verlof-end"
                value={form.end_date} min={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="v-daypart" className="text-xs">Dagdeel</Label>
            <select
              id="v-daypart" data-testid="portal-verlof-daypart"
              value={form.day_part}
              onChange={(e) => setForm((f) => ({ ...f, day_part: e.target.value }))}
              className="mt-1 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy/30"
            >
              <option value="hele_dag">Hele dag(en)</option>
              <option value="halve_dag">Halve dag</option>
            </select>
          </div>
          <div>
            <Label htmlFor="v-reason" className="text-xs">Toelichting (optioneel)</Label>
            <Input
              id="v-reason" data-testid="portal-verlof-reason"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="bv. zomervakantie"
            />
          </div>
          <Button
            type="button" onClick={submit} disabled={submitting}
            data-testid="portal-verlof-submit"
            className="w-full bg-brand-navy hover:bg-brand-navy/90 text-white"
          >
            {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
            Aanvraag versturen
          </Button>
          <p className="text-[10px] text-slate-400">
            Je werkgever bepaalt de definitieve uren-waardering bij goedkeuring. Ziekmelden gaat niet via verlof.
          </p>
        </div>
      </div>

      {/* My requests */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Mijn aanvragen</h3>
        {loading ? (
          <div className="py-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Laden…
          </div>
        ) : error ? (
          <div className="py-8 text-center text-rose-600 text-sm flex items-center justify-center gap-2">
            <AlertCircle size={16} /> Laden mislukt.
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center">
            <Plane size={26} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Nog geen verlofaanvragen.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="portal-verlof-list">
            {items.map((it) => {
              const st = STATUS[it.status] || STATUS.in_afwachting;
              return (
                <div key={it.id} data-testid={`portal-verlof-item-${it.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{TYPE_LABEL[it.type] || it.type}</p>
                      <p className="text-xs text-slate-500 tabular-nums">
                        {formatDateNL(it.start_date)}{it.end_date !== it.start_date ? ` – ${formatDateNL(it.end_date)}` : ''}
                        {it.day_part === 'halve_dag' ? ' · halve dag' : ''}
                      </p>
                      {it.notes && <p className="text-xs text-slate-500 mt-0.5">{it.notes}</p>}
                    </div>
                    <Badge className={`shrink-0 border ${st.cls}`} data-testid={`portal-verlof-status-${it.id}`}>{st.label}</Badge>
                  </div>
                  {it.status === 'in_afwachting' && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button" onClick={() => withdraw(it.id)}
                        data-testid={`portal-verlof-withdraw-${it.id}`}
                        className="text-xs text-rose-600 hover:bg-rose-50 rounded px-2 py-1 flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Intrekken
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function HistorieTab() {
  const VERZUIM_STATUS = {
    ziek: { label: 'Ziek', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    hersteld: { label: 'Hersteld', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  };
  const EVENT = {
    in_dienst: { label: 'In dienst', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    uit_dienst: { label: 'Uit dienst', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    re_indienst: { label: 'Opnieuw in dienst', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  };
  const [verzuim, setVerzuim] = useState(null);
  const [dienst, setDienst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const [v, d] = await Promise.all([
          api.get('/portal/my-verzuim'),
          api.get('/portal/my-dienstverband'),
        ]);
        if (!active) return;
        setVerzuim(v.data);
        setDienst(d.data);
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return <div className="py-12 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Laden…</div>;
  }
  if (error) {
    return <div className="py-12 text-center text-rose-600 text-sm flex items-center justify-center gap-2"><AlertCircle size={16} /> Laden mislukt.</div>;
  }

  const cur = dienst?.is_active ? EVENT.in_dienst : EVENT.uit_dienst;

  return (
    <div className="space-y-4" data-testid="portal-historie">
      {/* Dienstverband */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <Briefcase size={15} className="text-brand-navy" /> Dienstverband
          </h3>
          <Badge className={`border ${cur.cls}`} data-testid="portal-dienst-status">{dienst?.is_active ? 'In dienst' : 'Uit dienst'}</Badge>
        </div>
        {dienst?.datum_in_dienst && (
          <p className="text-xs text-slate-500 mb-3">In dienst sinds {formatDateNL(dienst.datum_in_dienst)}{dienst.datum_uit_dienst ? ` · uit dienst per ${formatDateNL(dienst.datum_uit_dienst)}` : ''}</p>
        )}
        {(!dienst?.events || dienst.events.length === 0) ? (
          <p className="py-4 text-center text-sm text-slate-500">Geen dienstverband-historie beschikbaar.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-2 space-y-3" data-testid="portal-dienst-list">
            {dienst.events.map((ev) => {
              const meta = EVENT[ev.type] || { label: ev.type, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
              return (
                <li key={ev.id} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-brand-navy" />
                  <div className="flex items-center gap-2">
                    <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
                    <span className="text-xs text-slate-500 tabular-nums">{formatDateNL(ev.effective_date)}</span>
                  </div>
                  {ev.reason && <p className="text-xs text-slate-500 mt-0.5">{ev.reason}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Verzuim */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
          <Stethoscope size={15} className="text-brand-navy" /> Verzuimhistorie
        </h3>
        {(!verzuim?.items || verzuim.items.length === 0) ? (
          <div className="py-6 text-center">
            <Stethoscope size={24} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen verzuim geregistreerd.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="portal-verzuim-list">
            {verzuim.items.map((v) => {
              const st = VERZUIM_STATUS[v.status] || { label: v.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
              return (
                <div key={v.id} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{v.reason || 'Verzuim'}</p>
                      <p className="text-xs text-slate-500 tabular-nums">
                        {formatDateNL(v.start_date)}{v.end_date ? ` – ${formatDateNL(v.end_date)}` : ' – heden'}
                      </p>
                    </div>
                    <Badge className={`shrink-0 border ${st.cls}`}>{st.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


export default function PortalRoster() {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const initialWeek = getISOWeek();
  const [year, setYear] = useState(initialWeek.year);
  const [week, setWeek] = useState(initialWeek.week);
  const [unread, setUnread] = useState(0);
  const [tab, setTab] = useState('rooster');

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/portal/me');
        setEmployee(me.data);
        try {
          const u = await api.get('/portal/my-notifications/unread-count');
          setUnread(u.data?.unread || 0);
        } catch (uErr) {
          console.warn('[portal] unread-count failed', uErr);
        }
      } catch (e) {
        if (e.response?.status === 401) navigate('/portaal/login', { replace: true });
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await api.post('/portal/logout');
    } catch (e) {
      console.warn('[portal] logout API failed', e);
    }
    navigate('/portaal/login', { replace: true });
  };

  if (!employee) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      <header className="bg-brand-navy text-white px-4 py-3 shadow-sm">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-base font-bold truncate">Hallo, {employee.name?.split(' ')[0]}</h1>
            <p className="text-[10px] opacity-80">#{employee.personeelsnummer}</p>
          </div>
          <div className="flex items-center gap-2">
            <ProbleemMeldenDialog compact locationScope={employee.home_location_id || null} />
            <button
              type="button"
              onClick={handleLogout}
              data-testid="portal-logout"
              className="rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs flex items-center gap-1.5"
            >
              <LogOut size={12} />
              Uitloggen
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-4">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'meldingen') setUnread(0); }}>
          <TabsList className="grid grid-cols-9 bg-white border border-slate-200 mb-4 p-1 h-auto">
            <TabsTrigger value="qr" data-testid="portal-tab-qr" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <QrCode size={14} className="mr-1" />QR
            </TabsTrigger>
            <TabsTrigger value="rooster" data-testid="portal-tab-rooster" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <CalendarDays size={14} className="mr-1" />Rooster
            </TabsTrigger>
            <TabsTrigger value="uren" data-testid="portal-tab-uren" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <ClipboardList size={14} className="mr-1" />Uren
            </TabsTrigger>
            <TabsTrigger value="verlof" data-testid="portal-tab-verlof" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <Plane size={14} className="mr-1" />Verlof
            </TabsTrigger>
            <TabsTrigger value="instructies" data-testid="portal-tab-instructies" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <BookOpen size={14} className="mr-1" />Instructies
            </TabsTrigger>
            <TabsTrigger value="meldingen" data-testid="portal-tab-meldingen" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs relative">
              <Bell size={14} className="mr-1" />Meldingen
              {unread > 0 && (
                <span
                  className="absolute top-0.5 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-semibold text-white"
                  data-testid="portal-meldingen-badge"
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profiel" data-testid="portal-tab-profiel" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <UserIcon size={14} className="mr-1" />Profiel
            </TabsTrigger>
            <TabsTrigger value="historie" data-testid="portal-tab-historie" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <History size={14} className="mr-1" />Historie
            </TabsTrigger>
            <TabsTrigger value="vacatures" data-testid="portal-tab-vacatures" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white py-2 text-xs">
              <Briefcase size={14} className="mr-1" />Vacatures
            </TabsTrigger>
          </TabsList>
          <TabsContent value="qr"><MijnQrTab /></TabsContent>
          <TabsContent value="rooster"><RoosterTab year={year} week={week} setYear={setYear} setWeek={setWeek} /></TabsContent>
          <TabsContent value="uren"><UrenTab year={year} week={week} setYear={setYear} setWeek={setWeek} /></TabsContent>
          <TabsContent value="verlof"><VerlofTab /></TabsContent>
          <TabsContent value="instructies"><InstructiesTab /></TabsContent>
          <TabsContent value="meldingen"><MeldingenTab /></TabsContent>
          <TabsContent value="profiel"><ProfielTab employee={employee} /></TabsContent>
          <TabsContent value="historie"><HistorieTab /></TabsContent>
          <TabsContent value="vacatures"><VacaturesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
